import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import React, { useState, useEffect, useCallback, useRef } from "react";

import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  Fade,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Badge,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import EmojiEmotionsRoundedIcon from "@mui/icons-material/EmojiEmotionsRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import VideoCallRoundedIcon from "@mui/icons-material/VideoCallRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import CircleIcon from "@mui/icons-material/Circle";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useChat } from "../contexts/ChatProvider";
import ModalNewChat from "./ModalNewChat";

// Styled Components - Teams inspired design
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    margin: "16px",
    maxHeight: "calc(100vh - 32px)",
    maxWidth: "calc(100vw - 32px)",
    animation: "slideIn 0.3s ease-out",
  },
  "@keyframes slideIn": {
    "0%": {
      opacity: 0,
      transform: "scale(0.95) translateY(-10px)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1) translateY(0)",
    },
  },
}));

const HeaderBar = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 50%, #4a148c 100%)",
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(90deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)",
    pointerEvents: "none",
  },
}));

const TitleSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

const ChatIconWrapper = styled(Box)({
  width: "36px",
  height: "36px",
  borderRadius: "8px",
  background: "rgba(255, 255, 255, 0.2)",
  backdropFilter: "blur(10px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.3)",
    transform: "scale(1.05)",
  },
});

const HeaderTitle = styled(Typography)({
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "16px",
  letterSpacing: "0.3px",
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
});

const HeaderSubtitle = styled(Typography)({
  color: "rgba(255, 255, 255, 0.8)",
  fontSize: "12px",
  fontWeight: 400,
});

const ControlsSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const AddButton = styled(IconButton)(({ theme }) => ({
  color: "#ffffff",
  padding: "8px 16px",
  borderRadius: "6px",
  background: "rgba(255, 255, 255, 0.2)",
  transition: "all 0.2s ease",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.3)",
    transform: "scale(1.02)",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.9)",
  padding: "8px",
  borderRadius: "6px",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "rgba(196, 49, 75, 0.9)",
    color: "#ffffff",
  },
}));

const StyledDialogContent = styled(DialogContent)({
  padding: 0,
  margin: 0,
  background: "#f5f5f5",
  display: "flex",
  height: "calc(100vh - 80px)",
});

// Chat specific styled components
const Sidebar = styled(Box)(({ theme }) => ({
  width: 320,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: "#fff",
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1),
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  "&:hover": {
    backgroundColor: theme.palette.grey[200],
  },
}));

const ChatList = styled(List)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: 0,
}));

const ChatListItem = styled(ListItem)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.grey[50],
  },
  "&.active": {
    backgroundColor: theme.palette.primary.light + "20",
    borderRight: `3px solid ${theme.palette.primary.main}`,
  },
}));

const ChatArea = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "white",
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: theme.spacing(1),
  backgroundColor: "#fafafa",
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isOwn",
})<{ isOwn: boolean }>(({ theme, isOwn }) => ({
  maxWidth: "70%",
  margin: theme.spacing(0.5, 0),
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(2),
  backgroundColor: isOwn ? "#7b1fa2" : "white",
  color: isOwn ? "white" : theme.palette.text.primary,
  alignSelf: isOwn ? "flex-end" : "flex-start",
  boxShadow: theme.shadows[1],
}));

const MessageInput = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  backgroundColor: "white",
}));

const InputField = styled(TextField)(({ theme }) => ({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(3),
  },
}));

const OnlineIndicator = styled(CircleIcon)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.success.main,
}));

type ModalChatProps = {
  onClose: () => void;
  open: boolean;
  title: string;
  processing: boolean;
};

const ModalChat = ({ onClose, open, title, processing }: ModalChatProps) => {
  const {
    sessions,
    activeSession,
    messages,
    loading,
    error,
    setActiveSession,
    sendMessage,
    currentUser,
    searchSessions,
    loadMessages,
  } = useChat();

  const [messageInput, setMessageInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredSessions, setFilteredSessions] = useState(sessions);
  const [newChatModalOpen, setNewChatModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Refs para scroll
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevMessagesLength = useRef(0);
  const isLoadingOlder = useRef(false);

  // Reset pagination quando mudar de sessão
  useEffect(() => {
    setCurrentPage(1);
    setHasMoreMessages(true);
    prevMessagesLength.current = 0;
    isLoadingOlder.current = false;
  }, [activeSession?.id]);

  // Scroll to bottom quando mensagens carregam
  useEffect(() => {
    if (messages.length > 0) {
      if (isLoadingOlder.current) {
        isLoadingOlder.current = false;
        return;
      }

      if (
        prevMessagesLength.current === 0 ||
        messages.length > prevMessagesLength.current
      ) {
        scrollToBottom();
      }
      prevMessagesLength.current = messages.length;
    }
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, []);

  const loadMoreMessages = useCallback(async () => {
    if (!activeSession || loadingMore || !hasMoreMessages) return;

    setLoadingMore(true);
    isLoadingOlder.current = true;

    const nextPage = currentPage + 1;

    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}api/chat/sessions/${activeSession.id}/messages?page=${nextPage}&limit=50`;
      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("rh-token")}`,
        },
      });

      if (response.ok) {
        const olderMessages = await response.json();

        if (olderMessages.length === 0) {
          setHasMoreMessages(false);
        } else {
          const container = messagesContainerRef.current;
          const previousScrollHeight = container?.scrollHeight || 0;

          await loadMessages(activeSession.id, nextPage);

          setCurrentPage(nextPage);

          setTimeout(() => {
            if (container) {
              const newScrollHeight = container.scrollHeight;
              container.scrollTop = newScrollHeight - previousScrollHeight;
            }
          }, 50);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar mais mensagens:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [activeSession, loadingMore, hasMoreMessages, currentPage, loadMessages]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;

      if (target.scrollTop < 100 && hasMoreMessages && !loadingMore) {
        loadMoreMessages();
      }
    },
    [hasMoreMessages, loadingMore, loadMoreMessages]
  );

  const handleSendMessage = async () => {
    if (messageInput.trim() && activeSession) {
      try {
        await sendMessage(messageInput.trim());
        setMessageInput("");
      } catch (error) {
        console.error("Erro ao enviar mensagem:", error);
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const getSessionDisplayName = (session: any) => {
    if (session.session_type === "external") {
      return session.candidate_name || session.session_name || "Contato externo";
    }
    if (session.session_type === "group") {
      return session.session_name || "Grupo sem nome";
    }
    const otherParticipant = session.participants?.find(
      (p: any) => p.user_id !== currentUser?.id
    );
    return otherParticipant?.nome || otherParticipant?.nomecomp || "Usuário";
  };

  const getSessionIcon = (session: any) => {
    if (session.session_type === "external") {
      if (session.external_type === "whatsapp") {
        return <WhatsAppIcon sx={{ color: "#25D366" }} />;
      }
      return <PhoneRoundedIcon />;
    }
    if (session.session_type === "group") {
      return <Typography variant="caption">G</Typography>;
    }
    return session.participants?.[0]?.user?.nome?.charAt(0) || "U";
  };

  const getLastMessageTime = (timestamp: string | undefined) => {
    if (!timestamp) return "";
    return format(new Date(timestamp), "HH:mm", { locale: ptBR });
  };

  // Filtrar sessões baseado na busca
  useEffect(() => {
    if (searchQuery.trim()) {
      setFilteredSessions(searchSessions(searchQuery));
    } else {
      setFilteredSessions(sessions);
    }
  }, [searchQuery, sessions, searchSessions]);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="chat-dialog-title"
      fullScreen
      maxWidth="xl"
      TransitionComponent={Fade as any}
      transitionDuration={300}
    >
      <HeaderBar>
        <TitleSection>
          <ChatIconWrapper>
            <ChatRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />
          </ChatIconWrapper>
          <Box>
            <HeaderTitle>Chat</HeaderTitle>
            <HeaderSubtitle>
              {sessions.length} conversa{sessions.length !== 1 ? "s" : ""}
            </HeaderSubtitle>
          </Box>
        </TitleSection>

        <ControlsSection>
          <Tooltip title="Nova Conversa" arrow>
            <AddButton size="small" onClick={() => setNewChatModalOpen(true)}>
              <AddRoundedIcon sx={{ fontSize: "18px" }} />
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#fff",
                  display: { xs: "none", sm: "block" },
                }}
              >
                Nova Conversa
              </Typography>
            </AddButton>
          </Tooltip>
          <Tooltip title="Fechar" arrow>
            <CloseButton size="small" onClick={onClose}>
              <CloseRoundedIcon sx={{ fontSize: "20px" }} />
            </CloseButton>
          </Tooltip>
        </ControlsSection>
      </HeaderBar>

      <StyledDialogContent>
        {/* Sidebar com lista de conversas */}
        <Sidebar>
          <SidebarHeader>
            <Typography variant="h6" fontWeight="bold">
              Conversas
            </Typography>
          </SidebarHeader>

          <SearchContainer>
            <SearchInput
              placeholder="Buscar conversas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              startAdornment={
                <SearchRoundedIcon sx={{ mr: 1, color: "grey.500" }} />
              }
            />
            <Tooltip title="Nova conversa">
              <IconButton
                size="small"
                onClick={() => setNewChatModalOpen(true)}
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                }}
              >
                <AddRoundedIcon />
              </IconButton>
            </Tooltip>
          </SearchContainer>

          <ChatList>
            {loading && (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            )}
            {error && (
              <Box p={2}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}
            {filteredSessions.map((session) => (
              <ChatListItem
                key={session.id}
                className={activeSession?.id === session.id ? "active" : ""}
                onClick={() => setActiveSession(session)}
              >
                <ListItemAvatar>
                  <Avatar
                    src={
                      session.candidate_avatar ||
                      session.session_avatar ||
                      undefined
                    }
                    sx={{
                      bgcolor:
                        session.session_type === "external"
                          ? "#25D366"
                          : "#7b1fa2",
                    }}
                  >
                    {!session.candidate_avatar &&
                      !session.session_avatar &&
                      getSessionIcon(session)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {getSessionDisplayName(session)}
                      </Typography>
                      {session.session_type === "external" && (
                        <Chip
                          label="WhatsApp"
                          size="small"
                          sx={{
                            bgcolor: "#25D366",
                            color: "white",
                            fontSize: "0.65rem",
                            height: 18,
                          }}
                        />
                      )}
                      {session.session_type === "private" && <OnlineIndicator />}
                    </Box>
                  }
                  secondary={
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {session.session_type === "external"
                          ? session.phone_number || "Contato externo"
                          : "Última mensagem..."}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" color="text.secondary">
                          {getLastMessageTime(session.last_message_at)}
                        </Typography>
                        {(session.unread_count ?? 0) > 0 && (
                          <Chip
                            size="small"
                            label={session.unread_count}
                            color="primary"
                            sx={{ height: 20, fontSize: "0.75rem" }}
                          />
                        )}
                      </Box>
                    </Box>
                  }
                />
              </ChatListItem>
            ))}
          </ChatList>
        </Sidebar>

        {/* Área principal do chat */}
        <ChatArea>
          {activeSession ? (
            <>
              {/* Header do chat */}
              <ChatHeader>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar
                    src={
                      activeSession.candidate_avatar ||
                      activeSession.session_avatar ||
                      undefined
                    }
                    sx={{
                      bgcolor:
                        activeSession.session_type === "external"
                          ? "#25D366"
                          : "#7b1fa2",
                      width: 48,
                      height: 48,
                    }}
                  >
                    {!activeSession.candidate_avatar &&
                      !activeSession.session_avatar &&
                      getSessionIcon(activeSession)}
                  </Avatar>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6" fontWeight="medium">
                        {getSessionDisplayName(activeSession)}
                      </Typography>
                      {activeSession.session_type === "external" && (
                        <Chip
                          label="WhatsApp"
                          size="small"
                          sx={{
                            bgcolor: "#25D366",
                            color: "white",
                            fontSize: "0.7rem",
                            height: 20,
                          }}
                        />
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      {activeSession.session_type === "external" ? (
                        <Typography variant="caption" color="text.secondary">
                          {activeSession.phone_number || "Contato externo"}
                        </Typography>
                      ) : activeSession.session_type === "private" ? (
                        <>
                          <OnlineIndicator />
                          <Typography variant="caption" color="text.secondary">
                            Online
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {activeSession.participants?.length || 0} participantes
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {activeSession.session_type !== "external" && (
                    <>
                      <Tooltip title="Chamada de voz">
                        <IconButton>
                          <PhoneRoundedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Chamada de vídeo">
                        <IconButton>
                          <VideoCallRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Informações">
                    <IconButton>
                      <InfoRoundedIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Mais opções">
                    <IconButton>
                      <MoreVertRoundedIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ChatHeader>

              {/* Container de mensagens */}
              <MessagesContainer
                ref={messagesContainerRef}
                onScroll={handleScroll}
              >
                {loadingMore && (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                <Box display="flex" flexDirection="column" gap={1}>
                  {messages.map((message) => (
                    <Box
                      key={message.id}
                      display="flex"
                      flexDirection="column"
                      alignItems={
                        message.sender_id === currentUser?.id
                          ? "flex-end"
                          : "flex-start"
                      }
                    >
                      <MessageBubble
                        isOwn={message.sender_id === currentUser?.id}
                      >
                        <Typography variant="body2">
                          {message.message_content}
                        </Typography>
                      </MessageBubble>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, mx: 1 }}
                      >
                        {message.sender_name || message.sender?.nome} •{" "}
                        {getLastMessageTime(message.sent_at)}
                      </Typography>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Box>
              </MessagesContainer>

              {/* Input de mensagem */}
              <MessageInput>
                <Tooltip title="Anexar arquivo">
                  <IconButton size="small">
                    <AttachFileRoundedIcon />
                  </IconButton>
                </Tooltip>
                <InputField
                  placeholder="Digite sua mensagem..."
                  multiline
                  maxRows={4}
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  variant="outlined"
                  size="small"
                />
                <Tooltip title="Emoji">
                  <IconButton size="small">
                    <EmojiEmotionsRoundedIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Enviar">
                  <IconButton
                    sx={{
                      bgcolor: "#7b1fa2",
                      color: "white",
                      "&:hover": { bgcolor: "#6a1b9a" },
                    }}
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim()}
                  >
                    <SendRoundedIcon />
                  </IconButton>
                </Tooltip>
              </MessageInput>
            </>
          ) : (
            // Estado vazio - nenhuma conversa selecionada
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              textAlign="center"
              gap={2}
            >
              <ChatRoundedIcon sx={{ fontSize: 80, color: "grey.300" }} />
              <Typography variant="h5" color="text.secondary">
                Selecione uma conversa
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Escolha uma conversa existente ou inicie uma nova
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => setNewChatModalOpen(true)}
                sx={{ bgcolor: "#7b1fa2", "&:hover": { bgcolor: "#6a1b9a" } }}
              >
                Nova Conversa
              </Button>
            </Box>
          )}
        </ChatArea>
      </StyledDialogContent>

      {/* Modal para nova conversa */}
      <ModalNewChat
        open={newChatModalOpen}
        onClose={() => setNewChatModalOpen(false)}
      />
    </StyledDialog>
  );
};

export default ModalChat;
